import { HttpClient, HttpHeaders } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, inject, DestroyRef } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';

type LockNode = {
  key?: string;
  mekey?: string;
  name?: string;
  selected?: boolean;
  children?: LockNode[];
  parent?: LockNode | null;
  collapsed?: boolean;
};

@Component({
    selector: 'gtw-locking',
    templateUrl: './locking.component.html',
    styleUrls: ['./locking.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LockingComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  loading = true;
  saving = false;
  searchText = '';
  nodes: LockNode[] = [];
  changedMap = new Map<string, { me_leid_name: string; me_leid: string; lock_y_n: 'Y' | 'N'; class_type: 'business' }>();
  private subs: Subscription[] = [];
  private currentScreenKey = '';

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private workspaceService: WorkspaceService,
    private globalService: GlobalService,
    private snack: GtwSnackbarService
  ) {}

  ngOnInit(): void {
    this.currentScreenKey = String(this.workspaceService.workspaceFactory.activeScreen?.screen_key || '');
    this.loadTree();

    this.subs.push(
      this.workspaceService.setTabActive.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        const activeKey = String(this.workspaceService.workspaceFactory.activeScreen?.screen_key || '');
        if (activeKey === this.currentScreenKey) {
          this.loadTree();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  trackByNode = (_: number, node: LockNode): string =>
    String(node?.mekey || node?.key || node?.name || '');

  toggleExpand(node: LockNode): void {
    node.collapsed = !node.collapsed;
  }

  onToggleNode(node: LockNode, checked: boolean): void {
    node.selected = checked;
    this.markChange(node, checked);
    this.setChildrenSelection(node, checked);
    this.updateParents(node.parent || null);
    this.cdr.markForCheck();
  }

  canShowNode(node: LockNode): boolean {
    if (!this.searchText) return true;
    const q = this.searchText.toLowerCase().trim();
    return this.matches(node, q);
  }

  save(): void {
    if (this.saving) return;

    const payload = Array.from(this.changedMap.values());
    if (!payload.length) {
      this.snack.openSnackBar('No lock changes to save.', 'gtw-snackbar--yellow');
      return;
    }

    const baseParams = this.getBaseParams();
    const filterParams = this.getFilterParams();
    const globalParams = this.globalService.global?.globalParams || {};
    const scenario = baseParams.scenario ?? filterParams?.scenario ?? globalParams?.scenario;
    const params: any = {
      tax_year: baseParams.tax_year,
      scenario,
      jcd_key: baseParams.jcd_key,
      lockedjsonObj: JSON.stringify(payload),
      issue_id: globalParams?.issue_id ?? '',
      issue_short_desc: globalParams?.issue_short_desc ?? '',
      checkLockedbj: JSON.stringify([{ scenario_key: Number(scenario) || 0 }]),
      scenarioCode:
        filterParams?.scenarioCode ??
        filterParams?.scenario_code ??
        globalParams?.scenarioCode ??
        'RAF',
      is_issue_key_enabled:
        filterParams?.is_issue_key_enabled ??
        globalParams?.is_issue_key_enabled ??
        'N',
    };

    const body = new URLSearchParams();
    Object.keys(params).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null) {
        body.set(k, String(params[k]));
      }
    });

    this.saving = true;
    this.cdr.markForCheck();

    this.http.post<any>(
      `${environment.base_url}/saveEntityBusinessConsolLock`,
      body.toString(),
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8' }),
        withCredentials: true,
      }
    ).pipe(take(1)).subscribe(
      (res: any) => {
        this.saving = false;
        if (res?.callSuccess === '1') {
          this.changedMap.clear();
          this.snack.openSnackBar('Business Lock saved successfully.', 'gtw-snackbar--green');
        } else {
          this.snack.openSnackBar(res?.errorMessage || 'Failed to save Business Lock.', 'gtw-snackbar--red');
        }
        this.cdr.markForCheck();
      },
      () => {
        this.saving = false;
        this.snack.openSnackBar('Failed to save Business Lock.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  private loadTree(): void {
    const baseParams = this.getBaseParams();
    const params: any = {
      tax_year: baseParams.tax_year,
      scenario: baseParams.scenario,
      jcd_key: baseParams.jcd_key,
    };

    this.loading = true;
    this.cdr.markForCheck();

    const body = new URLSearchParams();
    Object.keys(params).forEach((k) => {
      if (params[k] !== undefined && params[k] !== null && String(params[k]).trim() !== '') {
        body.set(k, String(params[k]));
      }
    });

    this.http.post<any>(
      `${environment.base_url}/getBusinssLockList`,
      body.toString(),
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8' }),
        withCredentials: true,
      }
    ).pipe(take(1)).subscribe(
      (res: any) => {
        const rows = Array.isArray(res?.jsonObject) ? res.jsonObject : [];
        this.nodes = rows.map((n: any) => this.prepareNode(n, null));
        this.changedMap.clear();
        this.loading = false;
        this.cdr.markForCheck();
      },
      () => {
        this.nodes = [];
        this.loading = false;
        this.snack.openSnackBar('Failed to load Business Lock hierarchy.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  private prepareNode(raw: any, parent: LockNode | null): LockNode {
    const node: LockNode = {
      ...raw,
      selected: this.toBool(raw?.selected),
      parent,
      collapsed: false,
      children: [],
    };

    const children = Array.isArray(raw?.children) ? raw.children : [];
    node.children = children.map((child: any) => this.prepareNode(child, node));
    return node;
  }

  private toBool(v: any): boolean {
    return v === true || v === 1 || v === '1' || v === 'Y' || v === 'y';
  }

  private setChildrenSelection(node: LockNode, checked: boolean): void {
    (node.children || []).forEach((child) => {
      child.selected = checked;
      this.markChange(child, checked);
      this.setChildrenSelection(child, checked);
    });
  }

  private updateParents(parent: LockNode | null): void {
    if (!parent) return;
    const allSelected = (parent.children || []).every((child) => !!child.selected);
    parent.selected = allSelected;
    this.markChange(parent, allSelected);
    this.updateParents(parent.parent || null);
  }

  private markChange(node: LockNode, checked: boolean): void {
    const mekey = String(node.mekey || node.key || '').trim();
    if (!mekey) return;

    this.changedMap.set(mekey, {
      me_leid_name: String(node.name || ''),
      me_leid: mekey,
      lock_y_n: checked ? 'Y' : 'N',
      class_type: 'business',
    });
  }

  private getFilterParams(): any {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    if (!activeScreen?.filters?.getFilterParams) return {};
    return activeScreen.filters.getFilterParams() || {};
  }

  private getBaseParams(): { tax_year: any; scenario: any; jcd_key: any } {
    const filterParams = this.getFilterParams();
    const globals = this.globalService.global?.globalParams || {};

    const taxYear = filterParams.tax_year ?? globals.tax_year;
    const resolvedScenario = filterParams.scenario
      ?? globals.scenario
      ?? (taxYear != null && this.globalService.getScenario ? this.globalService.getScenario(taxYear) : undefined);
    const jcdKey = globals.jcd_key;

    return {
      tax_year: taxYear,
      scenario: resolvedScenario,
      jcd_key: jcdKey,
    };
  }

  private matches(node: LockNode, q: string): boolean {
    const name = String(node?.name || '').toLowerCase();
    const mekey = String(node?.mekey || node?.key || '').toLowerCase();
    if (name.includes(q) || mekey.includes(q)) return true;
    return (node.children || []).some((c) => this.matches(c, q));
  }
}
